import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Hook personnalisé pour gérer la navigation vers les détails d'articles
 * @param {string} basePath - Chemin de base pour la navigation (par défaut: '/admin/article/details')
 * @returns {object} Fonctions de navigation
 */
export const useArticleNavigation = (basePath = "/admin/article/details") => {
  const navigate = useNavigate();

  /**
   * Génère un slug valide pour l'article
   * @param {object} article - L'objet article
   * @returns {string} Le slug généré
   */
  const generateSlug = useCallback((article) => {
    if (!article) return "";

    // Utiliser le slug existant ou l'ID comme fallback
    const slug = article.id_art;
    return slug || `article-${article.id_art}`;
  }, []);

  /**
   * Navigate vers la page de détails d'un article
   * @param {object} article - L'objet article
   * @param {object} options - Options de navigation
   */
  const navigateToDetails = useCallback(
    (article, options = {}) => {
      if (!article) {
        console.error("❌ Article manquant pour la navigation");
        return;
      }

      const finalSlug = generateSlug(article);
      const fullPath = `${basePath}/${finalSlug}`;

      // Log pour debugging (peut être désactivé en production)
      if (process.env.NODE_ENV === "development") {
        console.log(`🔗 Navigation vers: ${fullPath}`, {
          articleId: article.id_art,
          slug: finalSlug,
          title: article.title_art?.fr || article.title_art?.en || "Sans titre",
        });
      }

      // Options de navigation (replace, state, etc.)
      const navigationOptions = {
        replace: false,
        ...options,
      };

      if (navigationOptions.replace) {
        navigate(fullPath, { replace: true, state: navigationOptions.state });
      } else {
        navigate(fullPath, { state: navigationOptions.state });
      }
    },
    [navigate, basePath, generateSlug]
  );

  /**
   * Navigate vers la page d'édition d'un article
   * @param {object} article - L'objet article
   */
  const navigateToEdit = useCallback(
    (article) => {
      const finalSlug = generateSlug(article);
      const editPath = `/admin/article/edit/${finalSlug}`;

      if (process.env.NODE_ENV === "development") {
        console.log(`✏️ Navigation vers édition: ${editPath}`);
      }

      navigate(editPath);
    },
    [navigate, generateSlug]
  );

  /**
   * Navigate vers la liste des articles avec des filtres optionnels
   * @param {object} filters - Filtres à appliquer
   */
  const navigateToList = useCallback(
    (filters = {}) => {
      const searchParams = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          searchParams.set(key, value);
        }
      });

      const query = searchParams.toString();
      const listPath = `/admin/articles${query ? `?${query}` : ""}`;

      navigate(listPath);
    },
    [navigate]
  );

  return {
    navigateToDetails,
    navigateToEdit,
    navigateToList,
    generateSlug,
  };
};
